"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import { ChevronRight, FolderOpen } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import type { ReactNode } from "react";
import { ProjectStatusBadge } from "@/components/projects/project-status-badge";
import { ButtonLink } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Section } from "@/components/ui/section";
import { Skeleton } from "@/components/ui/skeleton";
import { metaTextClassName } from "@/components/ui/typography";
import { dateTimeFormat, relativeTimeFormat } from "@/lib/date-format";
import { cn } from "@/lib/utils";
function RecentActivitySection(t0) {
  const $ = _c(3);
  const {
    actions,
    children
  } = t0;
  let t1;
  if ($[0] !== actions || $[1] !== children) {
    t1 = <Section actions={actions} title="Atividade recente">{children}</Section>;
    $[0] = actions;
    $[1] = children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Most recently updated projects for the organization dashboard. */
export function RecentActivity(t0) {
  const $ = _c(19);
  const {
    subscriptionLocked: t1
  } = t0;
  const subscriptionLocked = t1 === undefined ? false : t1;
  const {
    organizationSlug
  } = useParams();
  let t2;
  if ($[0] !== subscriptionLocked) {
    t2 = subscriptionLocked ? "skip" : {
      limit: 8
    };
    $[0] = subscriptionLocked;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const recentProjects = useQuery(api.projects.queries.getRecentProjectsForDashboard, t2);
  const isLoading = !subscriptionLocked && recentProjects === undefined;
  if (!organizationSlug) {
    return null;
  }
  if (isLoading) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Skeleton className="h-8 w-24" />;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <RecentActivitySection actions={t3}><ul aria-busy="true" className="flex flex-col divide-y">{Array.from({
            length: 5
          }).map(_temp)}</ul></RecentActivitySection>;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  if (!recentProjects?.length) {
    const t3 = `/${organizationSlug}/projects/new`;
    let t4;
    if ($[4] !== t3) {
      t4 = <RecentActivitySection><EmptyState action={<ButtonLink variant="outline" href={t3}>Novo projeto</ButtonLink>} icon={FolderOpen} size="sm" title="Nenhuma atividade recente encontrada" /></RecentActivitySection>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  const t3 = `/${organizationSlug}/projects`;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronRight />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <ButtonLink size="sm" variant="outline" href={t3}>Ver todas{t4}</ButtonLink>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== organizationSlug || $[10] !== recentProjects) {
    let t7;
    if ($[12] !== organizationSlug) {
      t7 = project => {
        const updatedAt = new Date(project.updatedAt);
        return <li className="flex items-start gap-3 py-2.5" key={project._id}><FolderOpen className="text-muted-foreground mt-0.5 size-4 shrink-0" /><div className="flex min-w-0 flex-1 items-start justify-between gap-3"><div className="min-w-0"><div className="flex min-w-0 flex-wrap items-center gap-2"><Link className="text-foreground hover:text-primary truncate font-semibold" href={`/${organizationSlug}/projects/${project._id}`}>{project.projectCode}</Link><ProjectStatusBadge status={project.status} /></div>{project.producerName || project.harvest ? <p className="text-muted-foreground truncate">{[project.producerName, project.harvest].filter(Boolean).join(" \xB7 ")}</p> : null}</div><time className={cn(metaTextClassName, "shrink-0 whitespace-nowrap tabular-nums")} dateTime={updatedAt.toISOString()} title={dateTimeFormat(updatedAt)}>{relativeTimeFormat(updatedAt)}</time></div></li>;
      };
      $[12] = organizationSlug;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = recentProjects.map(t7);
    $[9] = organizationSlug;
    $[10] = recentProjects;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <ul className="flex flex-col divide-y">{t6}</ul>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <RecentActivitySection actions={t5}>{t7}</RecentActivitySection>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp(_, index) {
  return <li className="flex items-start gap-3 py-2.5" key={index}><Skeleton className="mt-0.5 size-4 shrink-0" /><div className="flex flex-1 flex-col gap-1.5"><Skeleton className="h-3.5 w-1/3" /><Skeleton className="h-3 w-1/4" /></div><Skeleton className="h-3 w-20" /></li>;
}